import { normalizeStorageUrl } from './apiBase';

import hero1 from '../assets/images/hero/hero-1.jpg';
import hero2 from '../assets/images/hero/hero-2.jpg';
import hero3 from '../assets/images/hero/hero-3.jpg';
import hero4 from '../assets/images/hero/hero-4.jpg';

const PROGRAM_HERO_CACHE_KEY = 'khayr_program_heroes_v4';
const memoryHeroBySlug = {};

export const PROGRAM_HERO_FALLBACKS = {
  kafala: hero1,
  saqya: hero2,
  omra: hero3,
  imaar: hero4,
  ramadan: hero4,
  caravane: hero4,
};

export const PRIORITY_PROGRAM_SLUGS = ['saqya', 'omra', 'kafala', 'ramadan'];

const emptyAssets = () => ({
  image_url: null,
  image_url_webp: null,
  image_url_webp_sm: null,
});

export const getProgramHeroImage = (imageUrl) => {
  if (!imageUrl) return null;
  return normalizeStorageUrl(imageUrl);
};

export const normalizeHeroAssets = (program) => {
  if (!program) return emptyAssets();
  const image_url = getProgramHeroImage(program.image_url);
  const image_url_webp = getProgramHeroImage(program.image_url_webp);
  const image_url_webp_sm = getProgramHeroImage(program.image_url_webp_sm);
  return {
    image_url: image_url || image_url_webp || image_url_webp_sm,
    image_url_webp,
    image_url_webp_sm,
  };
};

export const fastestHeroUrl = (assets) =>
  assets?.image_url_webp_sm || assets?.image_url_webp || assets?.image_url || null;

export const resolveProgramHeroImage = (slug, imageUrl, { allowFallback = false } = {}) => {
  const cms = getProgramHeroImage(imageUrl);
  if (cms) return cms;
  return allowFallback ? PROGRAM_HERO_FALLBACKS[slug] || null : null;
};

export const readProgramHeroCache = (slug) => {
  try {
    const raw = localStorage.getItem(PROGRAM_HERO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.[slug] || null;
  } catch {
    return null;
  }
};

export const writeProgramHeroCache = (slug, program) => {
  if (!program || !slug) return;
  const assets = normalizeHeroAssets(program);
  const prev = memoryHeroBySlug[slug] || emptyAssets();
  const imageChanged = Boolean(assets.image_url && prev.image_url && assets.image_url !== prev.image_url);
  const next = imageChanged
    ? {
        image_url: assets.image_url,
        image_url_webp: assets.image_url_webp,
        image_url_webp_sm: assets.image_url_webp_sm,
      }
    : {
        image_url: assets.image_url || prev.image_url,
        image_url_webp: assets.image_url_webp || prev.image_url_webp,
        image_url_webp_sm: assets.image_url_webp_sm || prev.image_url_webp_sm,
      };
  if (next.image_url || next.image_url_webp) {
    memoryHeroBySlug[slug] = next;
  }
  try {
    const raw = localStorage.getItem(PROGRAM_HERO_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const cached = parsed[slug] || {};
    parsed[slug] = {
      title: program.title ?? cached.title,
      description: program.description ?? cached.description,
      image_url: next.image_url,
      image_url_webp: next.image_url_webp,
      image_url_webp_sm: next.image_url_webp_sm,
      hero_cta_text: program.hero_cta_text ?? cached.hero_cta_text,
      hero_tagline: program.hero_tagline ?? cached.hero_tagline,
      sub_nav_items: program.sub_nav_items ?? cached.sub_nav_items,
      donate_title: program.donate_title ?? cached.donate_title,
      donate_text: program.donate_text ?? cached.donate_text,
      donate_button_text: program.donate_button_text ?? cached.donate_button_text,
      kafala_banner_text: program.kafala_banner_text ?? cached.kafala_banner_text,
      kafala_conditions_title: program.kafala_conditions_title ?? cached.kafala_conditions_title,
      kafala_conditions: program.kafala_conditions ?? cached.kafala_conditions,
      video_url: program.video_url ?? cached.video_url,
      video_embed_src: program.video_embed_src ?? cached.video_embed_src,
      video_file_url: program.video_file_url ?? cached.video_file_url,
    };
    localStorage.setItem(PROGRAM_HERO_CACHE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore quota errors
  }
};

export const mergeProgramWithCache = (slug, data) => {
  const cached = readProgramHeroCache(slug);
  const base = data ? { ...data, slug } : cached ? { slug, ...cached } : null;
  if (!base) return null;

  const dataUrl = getProgramHeroImage(data?.image_url);
  const cachedUrl = cached?.image_url;
  const imageChanged = Boolean(dataUrl && cachedUrl && dataUrl !== cachedUrl);
  const assets = normalizeHeroAssets({
    image_url: dataUrl || cachedUrl || base.image_url,
    image_url_webp: imageChanged ? data?.image_url_webp : (data?.image_url_webp || cached?.image_url_webp),
    image_url_webp_sm: imageChanged ? data?.image_url_webp_sm : (data?.image_url_webp_sm || cached?.image_url_webp_sm),
  });

  return {
    ...base,
    slug,
    ...assets,
  };
};

export const getProgramHeroAssets = (slug) => {
  if (memoryHeroBySlug[slug]) {
    return memoryHeroBySlug[slug];
  }
  const cached = readProgramHeroCache(slug);
  if (cached) {
    const assets = normalizeHeroAssets(cached);
    if (assets.image_url || assets.image_url_webp) {
      memoryHeroBySlug[slug] = assets;
      return assets;
    }
  }
  return emptyAssets();
};

export const seedProgramHeroMemory = (programmes) => {
  if (!Array.isArray(programmes)) return;
  programmes.forEach((p) => {
    if (!p?.slug) return;
    const assets = normalizeHeroAssets(p);
    if (assets.image_url || assets.image_url_webp) {
      memoryHeroBySlug[p.slug] = assets;
    }
  });
};

export const getProgramHeroUrl = (slug) => {
  const assets = getProgramHeroAssets(slug);
  return fastestHeroUrl(assets) || assets.image_url || null;
};

export const seedProgramHeroCache = (programmes) => {
  if (!Array.isArray(programmes)) return;
  seedProgramHeroMemory(programmes);
  programmes.forEach((p) => {
    if (p?.slug && (p.image_url || p.image_url_webp)) {
      writeProgramHeroCache(p.slug, p);
    }
  });
};

/** Charge le cache localStorage en mémoire au démarrage (avant le 1er rendu React). */
export const bootstrapProgramHeroCache = () => {
  try {
    const raw = localStorage.getItem(PROGRAM_HERO_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const urls = [];
    Object.entries(parsed).forEach(([slug, data]) => {
      const assets = normalizeHeroAssets(data);
      if (assets.image_url || assets.image_url_webp) {
        memoryHeroBySlug[slug] = assets;
        const url = fastestHeroUrl(assets);
        if (url) urls.push(url);
      }
    });
    return urls;
  } catch {
    return [];
  }
};
